import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faCalendar, faDollarSign, faUsers, faPhone, faGlobe, faArrowLeft, faClock, faTag, faUser, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { Button, Spinner, Badge, Alert } from '@themesberg/react-bootstrap';
import { GetApiRequest, resolveImageUrl } from "../services/api";
import { toast } from '../components/Toaster';

export default () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [experience, setExperience] = useState(null);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const fetchExperienceDetails = async (experienceId) => {
    setLoading(true);
    setError(null);
    setExperience(null);
    try {
      const response = await GetApiRequest(`experiences/${experienceId}`);
      if (response.data?.success && response.data?.data?.experience) {
        setExperience(response.data.data.experience);
      } else {
        setError('Failed to load experience details');
      }
    } catch (err) {
      console.error('Failed to fetch experience details:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load experience details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }; 

  useEffect(() => {
    if (id) fetchExperienceDetails(id);
  }, [id]);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [experience?._id]);

  const formatEventTime = (timeValue) => {
    if (timeValue == null || timeValue === '') return null;
    const s = String(timeValue).trim();
    if (/^\d{1,2}:\d{2}$/.test(s)) {
      const [h, m] = s.split(':').map(Number);
      const d = new Date(2000, 0, 1, h, m, 0);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    }
    const d = new Date(timeValue);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  /** Format open/close time (HH:mm or legacy ISO) for display. */
  const formatTimeDisplay = (timeValue) => {
    if (timeValue == null || timeValue === '') return null;
    const s = String(timeValue).trim();
    if (/^\d{1,2}:\d{2}$/.test(s)) {
      const [h, m] = s.split(':').map(Number);
      const d = new Date(2000, 0, 1, h, m, 0);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    }
    const d = new Date(timeValue);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatEventDate = (isoDate) => {
    if (!isoDate) return null;
    return new Date(isoDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Food": "danger",
      "Events": "primary",
      "Outdoors": "success",
      "Nightlife": "warning",
      "Wellness": "info",
      "Art": "secondary",
      "Hidden Gems": "dark",
      "Family": "info"
    };
    return colors[category] || "secondary";
  };

  // Resolve image URLs: prefer fileAssets (high/medium), fallback to legacy images array.
  // All URLs are passed through resolveImageUrl to proxy Google Places photos server-side.
  const imageUrls = React.useMemo(() => {
    if (experience?.fileAssets?.length) {
      return experience.fileAssets.map((asset) => ({
        main: resolveImageUrl(asset?.urls?.high || asset?.urls?.medium || asset?.urls?.thumbnail || asset?.urls?.original),
        thumb: resolveImageUrl(asset?.urls?.medium || asset?.urls?.thumbnail || asset?.urls?.low || asset?.urls?.original || asset?.urls?.high),
      })).filter((u) => u.main);
    }
    if (experience?.images?.length) {
      return experience.images.map((url) => ({ main: resolveImageUrl(url), thumb: resolveImageUrl(url) }));
    }
    return [];
  }, [experience?.fileAssets, experience?.images]);

  const mainImageUrl = imageUrls.length > 0 ? imageUrls[0].main : null;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }} className="mb-3" />
          <p style={{ color: '#64748b' }}>Loading experience details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <Button 
            style={{ 
              background: 'none', 
              border: '1px solid #e2e8f0', 
              color: '#475569', 
              borderRadius: '8px',
              padding: '0.6rem 1.25rem',
              fontWeight: 600,
              transition: 'all 0.3s ease'
            }}
            onClick={() => history.goBack()}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </Button>
        </div>
        <Alert style={{ 
          background: '#fef2f2', 
          border: '1px solid #fecaca', 
          color: '#991b1b',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <Alert.Heading style={{ color: '#991b1b', marginBottom: '0.75rem' }}>Error Loading Experience</Alert.Heading>
          <p className="mb-0">{error}</p>
        </Alert>
      </div>
    );
  }

  if (!experience) {
    return (
      <div style={{ padding: '2rem 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <Button 
            style={{ 
              background: 'none', 
              border: '1px solid #e2e8f0', 
              color: '#475569', 
              borderRadius: '8px',
              padding: '0.6rem 1.25rem',
              fontWeight: 600,
              transition: 'all 0.3s ease'
            }}
            onClick={() => history.goBack()}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back
          </Button>
        </div>
        <Alert style={{ 
          background: '#fef3c7', 
          border: '1px solid #fde68a', 
          color: '#92400e',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <Alert.Heading style={{ color: '#92400e', marginBottom: '0.75rem' }}>Experience Not Found</Alert.Heading>
          <p className="mb-0">The requested experience could not be found.</p>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 0' }}>
      <style>{`
        .detail-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .back-btn {
          background: none;
          border: 1px solid #e2e8f0;
          color: #475569;
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .back-btn:hover {
          background: #f8fafc;
          border-color: #4f46e5;
          color: #4f46e5;
        }

        .detail-page-title {
          font-size: 1.875rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .image-gallery {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
        }

        .main-image {
          width: 100%;
          height: 400px;
          object-fit: cover;
          display: block;
        }

        .thumbnail-gallery {
          display: flex;
          gap: 0.75rem;
          padding: 1rem;
          background: #f8fafc;
          overflow-x: auto;
        }

        .thumbnail-img {
          width: 100px;
          height: 100px;
          border-radius: 8px;
          object-fit: cover;
          cursor: pointer;
          border: 2px solid transparent;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .thumbnail-img:hover {
          border-color: #4f46e5;
          transform: scale(1.05);
        }

        .thumbnail-img-active {
          border-color: #4f46e5;
          box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.3);
        }

        .detail-card {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
        }

        .detail-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.75rem;
        }

        .detail-card h5 {
          font-size: 1rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1rem;
          margin-top: 1.5rem;
        }

        .detail-card h5:first-child {
          margin-top: 0;
        }

        .detail-description {
          color: #64748b;
          font-size: 1rem;
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }

        .badges-group {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-bottom: 1.5rem;
        }

        .detail-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .info-row {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .info-row:last-child {
          border-bottom: none;
        }

        .info-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .info-content {
          flex: 1;
        }

        .info-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 0.25rem;
        }

        .info-value {
          font-size: 1rem;
          color: #1e293b;
          font-weight: 500;
        }

        .info-value a {
          color: #4f46e5;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .info-value a:hover {
          text-decoration: underline;
          color: #4338ca;
        }

        .opening-hours-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .hour-item {
          background: #f8fafc;
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .hour-day {
          font-weight: 700;
          color: #1e293b;
          text-transform: capitalize;
          margin-bottom: 0.5rem;
          font-size: 0.95rem;
        }

        .hour-time {
          color: #64748b;
          font-size: 0.9rem;
        }

        .hour-closed {
          color: #ef4444;
          font-weight: 600;
        }

        .tags-group {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .tag-badge {
          background: #f1f5f9;
          color: #4f46e5;
          padding: 0.4rem 0.8rem;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .creator-info {
          background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
          color: white;
          padding: 1.5rem;
          border-radius: 12px;
        }

        .creator-info h6 {
          font-weight: 700;
          margin-bottom: 1rem;
          font-size: 1rem;
        }

        .creator-detail {
          font-size: 0.95rem;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .creator-detail a {
          color: #ffffff;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .creator-detail a:hover {
          text-decoration: underline;
        }

        @media (max-width: 768px) {
          .main-image {
            height: 250px;
          }

          .detail-card {
            padding: 1.5rem;
          }

          .opening-hours-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Header */}
      <div className="detail-header">
        <Button 
          className="back-btn"
          onClick={() => history.goBack()}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Experiences
        </Button>
        <h2 className="detail-page-title">Experience Details</h2>
        <div style={{ width: '140px' }} />
      </div>

      {/* Image Gallery - fileAssets (high/medium) or legacy images */}
      <div className="image-gallery">
        {mainImageUrl ? (
          <>
            <img
              src={imageUrls[selectedImageIndex]?.main || mainImageUrl}
              alt={experience.title}
              className="main-image"
            />
            {imageUrls.length > 1 && (
              <div className="thumbnail-gallery">
                {imageUrls.map((item, i) => (
                  <img
                    key={i}
                    src={item.thumb}
                    alt={`Gallery ${i + 1}`}
                    className={`thumbnail-img ${selectedImageIndex === i ? 'thumbnail-img-active' : ''}`}
                    onClick={() => setSelectedImageIndex(i)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{
            width: '100%',
            height: '400px',
            background: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#cbd5e0'
          }}>
            <FontAwesomeIcon icon={faTag} style={{ fontSize: '3rem' }} />
          </div>
        )}
      </div>

      {/* Main Details Card */}
      <div className="detail-card">
        <h3>{experience.title}</h3>
        <p className="detail-description">{experience.description}</p>

        {/* Badges */}
        <div className="badges-group">
          <Badge bg={getCategoryColor(experience.category)} style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
            <FontAwesomeIcon icon={faTag} className="me-1" />
            {experience.category}
          </Badge>
          {experience.grouptype && experience.grouptype.length > 0 && (
            <Badge bg="info" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
              <FontAwesomeIcon icon={faUsers} className="me-1" />
              {experience.grouptype.join(', ')}
            </Badge>
          )}
          <Badge bg="success" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
            <FontAwesomeIcon icon={faDollarSign} className="me-1" />
            {experience.price || 'N/A'}
          </Badge>
        </div>

        {/* Info Section */}
        <h5>Information</h5>
        <div>
          {experience.location?.address && (
            <div className="info-row">
              <div className="info-icon" style={{ background: '#fecaca', color: '#dc2626' }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <div className="info-content">
                <div className="info-label">Location</div>
                <div className="info-value">{experience.location.address}</div>
              </div>
            </div>
          )}

          {experience.datetime && (
            <div className="info-row">
              <div className="info-icon" style={{ background: '#bfdbfe', color: '#2563eb' }}>
                <FontAwesomeIcon icon={faCalendar} />
              </div>
              <div className="info-content">
                <div className="info-label">Date & Time</div>
                <div className="info-value">
                  {new Date(experience.datetime).toLocaleString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </div>
              </div>
            </div>
          )}

          {experience.phone && (
            <div className="info-row">
              <div className="info-icon" style={{ background: '#c7d2fe', color: '#4f46e5' }}>
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div className="info-content">
                <div className="info-label">Phone</div>
                <div className="info-value">
                  <a href={`tel:${experience.phone}`}>{experience.phone}</a>
                </div>
              </div>
            </div>
          )}

          {experience.website && (
            <div className="info-row">
              <div className="info-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                <FontAwesomeIcon icon={faGlobe} />
              </div>
              <div className="info-content">
                <div className="info-label">Website</div>
                <div className="info-value">
                  <a href={experience.website} target="_blank" rel="noreferrer">{experience.website}</a>
                </div>
              </div>
            </div>
          )}

          {experience.bookingLink && (
            <div className="info-row">
              <div className="info-icon" style={{ background: '#f3e8ff', color: '#a855f7' }}>
                <FontAwesomeIcon icon={faGlobe} />
              </div>
              <div className="info-content">
                <div className="info-label">Booking Link</div>
                <div className="info-value">
                  <a href={experience.bookingLink} target="_blank" rel="noreferrer">Book Now</a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tags */}
        {experience.tags && experience.tags.length > 0 && (
          <>
            <h5>Tags</h5>
            <div className="tags-group">
              {experience.tags.map((tag, i) => (
                <span key={i} className="tag-badge">#{tag}</span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Special Event Hours (when hoursType === 'special') */}
      {experience.hoursType === 'special' && experience.specialEventHours && experience.specialEventHours.length > 0 && (
        <div className="detail-card">
          <h5>
            <FontAwesomeIcon icon={faClock} className="me-2" />
            Event Hours
          </h5>
          <div className="opening-hours-grid">
            {experience.specialEventHours.map((event, idx) => {
              const dateStr = formatEventDate(event.date);
              const startStr = formatEventTime(event.startTime);
              const endStr = formatEventTime(event.endTime);
              if (!dateStr || !startStr) return null;
              const timeLabel = endStr ? `${startStr} – ${endStr}` : startStr;
              return (
                <div key={idx} className="hour-item">
                  <div className="hour-day">{dateStr}</div>
                  <div className="hour-time">{timeLabel}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Regular Opening Hours (when not special event) */}
      {experience.hoursType !== 'special' && experience.openingHours && Object.keys(experience.openingHours).length > 0 && (
        <div className="detail-card">
          <h5>
            <FontAwesomeIcon icon={faClock} className="me-2" />
            Opening Hours
          </h5>
          <div className="opening-hours-grid">
            {Object.entries(experience.openingHours).map(([day, info]) => (
              <div key={day} className="hour-item">
                <div className="hour-day">{day}</div>
                <div className="hour-time">
                  {info.closed ? (
                    <span className="hour-closed">Closed</span>
                  ) : (
                    <>
                      {info.open != null && info.open !== '' && info.close != null && info.close !== '' && (
                        <>
                          {formatTimeDisplay(info.open)} – {formatTimeDisplay(info.close)}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {experience.experienceHistory && experience.experienceHistory.length > 0 && (
        <div className="detail-card">
          <h5>History</h5>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Audit log (newest first). Shown when you are allowed to view this experience as staff or owner.
          </p>
          <ul className="list-unstyled mb-0" style={{ fontSize: '0.9rem' }}>
            {[...experience.experienceHistory]
              .sort(
                (a, b) =>
                  new Date(b.createdAt || 0).getTime() -
                  new Date(a.createdAt || 0).getTime()
              )
              .map((entry, idx, arr) => (
                <li
                  key={`${entry.createdAt}-${idx}`}
                  className="mb-3 pb-3"
                  style={{
                    borderBottom: idx < arr.length - 1 ? '1px solid #f1f5f9' : 'none',
                  }}
                >
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.35rem' }}>
                    {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '—'}
                  </div>
                  <div style={{ color: '#334155', whiteSpace: 'pre-wrap' }}>{entry.text}</div>
                </li>
              ))}
          </ul>
        </div>
      )}

      {/* Creator Info */}
      {experience.createdBy && (
        <div className="creator-info">
          <h6>
            <FontAwesomeIcon icon={faUser} className="me-2" />
            Created By
          </h6>
          <div className="creator-detail">
            {experience.createdBy.name || 'Unknown'}
          </div>
          {experience.createdBy.email && (
            <div className="creator-detail">
              <FontAwesomeIcon icon={faEnvelope} />
              <a href={`mailto:${experience.createdBy.email}`}>{experience.createdBy.email}</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};